import { CircularProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';

import { useGetVirtualAccountQuery } from '@/api/v1';
import { FiatAccountDetailsContent } from '@/components/organisms/FiatAccountDetailsContent';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { PaymentRailType } from '@/types/currency';

interface Props {
  readonly paymentRail: PaymentRailType.ACH | PaymentRailType.Wire | PaymentRailType.Swift;
}

export const FiatAccountDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const { paymentRail } = props;

  const customer = useCustomer();
  const tr = useTranslator();

  const {
    data: paymentInstructions,
    isError,
    isFetching,
  } = useGetVirtualAccountQuery(
    {
      beneficiaryId: customer.beneficiaryId ?? '',
      paymentRail: paymentRail,
    },
    {
      skip: !customer.beneficiaryId,
    },
  );

  if (isFetching) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.circularProgress}>
          <CircularProgress size={64} thickness={4} color="inherit" />
        </Box>
      </Box>
    );
  } else if (isError) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body2" color="custom.LightGray">
          {tr('ACCOUNT_INFORMATION_ERROR')}
        </Typography>
      </Box>
    );
  } else if (paymentInstructions) {
    return (
      <Box sx={styles.container}>
        <FiatAccountDetailsContent paymentInstructions={paymentInstructions} />
      </Box>
    );
  } else {
    return <></>;
  }
};

const styles: Record<string, any> = {
  container: {
    mt: 3,
    px: 4,
    py: 3,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
  },
  circularProgress: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 120,
  },
};
